// react-scan must be imported before React
import { scan } from "react-scan";

if (import.meta.env.DEV) {
    scan({ enabled: false, showToolbar: true });
}

// ensure to bind ipc as first thing
import "@/ipc";

// Initialize Sentry before app.tsx so tracing is active before the router is created
import "@/sentry";

// then import other things
import { GitpodAPI } from "@/api";
import { App } from "@/app";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { Toaster } from "@/components/podkit/toasts/Toaster";
import { GitpodAPIContext } from "@/contexts/GitpodAPIContext";
import { createSegment } from "@/contexts/SegmentContext";
import { SegmentProvider } from "@/contexts/SegmentProvider";
import { restorePrincipal } from "@/principal";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConfigCatProvider } from "configcat-react";
import React from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "@/contexts/ThemeProvider";
import "@/index.css";
import { DesktopProvider } from "@/contexts/DesktopProvider";
import { appMainService, runnerMainService } from "@/ipc";
import { startVersionTracker } from "@/version-tracker";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { QueryClientProvider } from "@/components/QueryClientProvider";
import { localFlagOverrides } from "@/feature-flags";
import { ConfettiContextProvider } from "@/contexts/ConfettiContext";
import { ShareResourceModalProvider } from "@/contexts/ShareResourceContext";

// Restoring principal from local storage before warming up the query client here.
restorePrincipal();

// Start tracking version of the management plane
startVersionTracker();

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <QueryClientProvider>
            <SegmentProvider create={() => createSegment()}>
                <ConfigCatProvider
                    sdkKey={"configcat-proxy/default"}
                    options={{
                        baseUrl: window.origin + "/feature-flags/configcat",
                        flagOverrides: localFlagOverrides(),
                        setupHooks: (hooks) => hooks.on("clientReady", () => console.log("ConfigCat client is ready!")),
                    }}
                >
                    <DesktopProvider
                        runnerService={runnerMainService}
                        isDesktop={!!runnerMainService}
                        appMainService={appMainService}
                    >
                        <GitpodAPIContext.Provider value={GitpodAPI}>
                            {/* Currently the ThemeProvider needs to be a child of the API provider, as we rely on the users identity to determine the theme based on our feature flag settings */}
                            <ThemeProvider>
                                <ExceptionErrorBoundary>
                                    <ConfettiContextProvider>
                                        <ShareResourceModalProvider>
                                            <NuqsAdapter>
                                                <ReactQueryDevtools initialIsOpen={false} />
                                                <App />
                                                <Toaster />
                                            </NuqsAdapter>
                                        </ShareResourceModalProvider>
                                    </ConfettiContextProvider>
                                </ExceptionErrorBoundary>
                            </ThemeProvider>
                        </GitpodAPIContext.Provider>
                    </DesktopProvider>
                </ConfigCatProvider>
            </SegmentProvider>
        </QueryClientProvider>
    </React.StrictMode>,
);
